import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { Oval } from "react-loader-spinner";
import { useAffiliate } from "../../hooks/useAffiliate";
import { selectUploadedImageIds, setCustomizedProduct, setPetType } from "../../store/customizerSlice";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "../../config/config";
import { useGetAffiliateProductsIdsQuery, useGetAffiliateProductsMetaQuery, useGetProductsQuery } from "../../store/apiSlice";
import ProductBox from "./ProductBox";
import ProductDetailsModal from "./ProductDetailsModal";
import { addToCart, openCart } from "../../store/cartSlice";
import useProductModal from "../../hooks/useProductModal";
import { getImageFromDB, saveImageToDB } from "../../utils/indexedDB";
import { fbqTrack } from "../../utils/eventsTracking/fbq";
import { gtagEvent } from "../../utils/eventsTracking/gtag";
import { trackCustomEvent } from "../../utils/eventsTracking/trackCustomEvent";
import { extractAffiliateCode } from "../../utils/extractAffiliateCode";
import { useLocation } from "react-router";

const productsGrouped = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
};

const AffiliateProducts = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const ref = extractAffiliateCode(location);
  const uploadedImageIds = useSelector(selectUploadedImageIds);
  const { affiliate, isLoading: isAffiliateLoading } = useAffiliate();
  const { data: products, isLoading: isProductsLoading } = useGetProductsQuery();
  const { data: products_meta, isLoading: isProductsMetaLoading } = useGetAffiliateProductsMetaQuery(ref);
  const { data: enabledProductIds, isLoading: isProductIdsLoading } = useGetAffiliateProductsIdsQuery(ref);

  const canvasRef = useRef(null);
  const [subscriptions, setSubscriptions] = useState({});
  const [finalImages, setFinalImages] = useState({});
  const [resultImageIds, setResultImageIds] = useState({});
  const [allImages, setAllImages] = useState([]);
  const [affiliatePreviewImage, setAffiliatePreviewImage] = useState(null);
  const [loadingAffiliatePreviewImage, setLoadingAffiliatePreviewImage] = useState(false);

  const customMetaByProductId = useMemo(() => {
    if (!products_meta || !Array.isArray(products_meta)) return {};
    return products_meta.reduce((acc, meta) => {
      const { product_id, type, value } = meta;
      if (!acc[product_id]) acc[product_id] = {};
      acc[product_id][type] = value;
      return acc;
    }, {});
  }, [products_meta]);


  const filteredProducts = useMemo(() => {
    if (!products || !affiliate || isProductIdsLoading) return [];

    let filtered = products;

    if (enabledProductIds) {
      filtered = filtered.filter(product =>
        enabledProductIds.includes(product.id)
      );
    }

    const animalTypes = affiliate.shop_animal_type;
    if (Array.isArray(animalTypes)) {
      filtered = filtered.filter(product =>
        animalTypes.includes(product.petType?.toLowerCase())
      );
    }


    return filtered;
  }, [products, affiliate, enabledProductIds]);



  const {
    isOpen: isProductModalActive,
    modalData: productDetailsModalFor,
    openModal,
    closeModal
  } = useProductModal();

  const toggleSubscription = (productId) => {
    setSubscriptions((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };
  useEffect(() => {
    const loadAffiliateImage = async () => {
      if (!isProductModalActive || !productDetailsModalFor?.ID) return;

      const productId = productDetailsModalFor.id;
      const resultAffiliateImageId = resultImageIds?.[productId];

      setAffiliatePreviewImage(null);

      if (!resultAffiliateImageId) return;

      setLoadingAffiliatePreviewImage(true);

      try {
        const blob = await getImageFromDB("onProductImages", null, {
          productId,
          resultImageId: resultAffiliateImageId,
        });

        if (blob) {
          const objectUrl = URL.createObjectURL(blob);
          setAffiliatePreviewImage(objectUrl);

          return () => URL.revokeObjectURL(objectUrl);
        } else {
          setAffiliatePreviewImage(null);
        }
      } catch (e) {
        console.error("Failed to load affiliate image:", e);
        setAffiliatePreviewImage(null);
      } finally {
        setLoadingAffiliatePreviewImage(false);
      }
    };

    loadAffiliateImage();
  }, [isProductModalActive, productDetailsModalFor?.ID, resultImageIds]);


  useEffect(() => {
    if (!products || isProductsLoading || !affiliate || isAffiliateLoading || isProductsMetaLoading || isProductIdsLoading || !affiliate.logo) return;

    const applyAffiliateLogoToProducts = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      let updatedImages = {};

      for (const product of filteredProducts) {
        const productLabel = customMetaByProductId?.[product.id]?.label || affiliate.label;

        const resultAffiliateImageId = `${affiliate.code}_${product.id}_affiliate`;

        let resultImageBlob = await getImageFromDB("resultImages", resultAffiliateImageId);
        if (!resultImageBlob) {
          const tempImage = new Image();
          tempImage.src = affiliate.logo;
          tempImage.crossOrigin = "anonymous";

          await new Promise((resolve) => {
            tempImage.onload = async () => {
              canvas.width = CANVAS_WIDTH;
              canvas.height = CANVAS_HEIGHT;

              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

              let logoWidth = CANVAS_WIDTH * 0.8;
              let logoHeight = (logoWidth * tempImage.height) / tempImage.width;
              let logoX = (CANVAS_WIDTH - logoWidth) / 2;
              let logoY = (CANVAS_HEIGHT - logoHeight) / 2;

              if (productLabel) {
                logoWidth = CANVAS_WIDTH * 0.6;
                logoHeight = (logoWidth * tempImage.height) / tempImage.width;
                logoX = (CANVAS_WIDTH - logoWidth) / 2;
                logoY += CANVAS_HEIGHT * 0.15;

                const overlayImage = new Image();
                overlayImage.src = productLabel;
                overlayImage.crossOrigin = "anonymous";

                await new Promise((res) => {
                  overlayImage.onload = () => {
                    ctx.drawImage(overlayImage, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                    res();
                  };
                });
              }

              if (affiliate.apply_logo_on_label) {
                ctx.drawImage(tempImage, logoX, logoY, logoWidth, logoHeight);
              }

              canvas.toBlob(async (blob) => {
                if (!blob) return resolve();
                await saveImageToDB("resultImages", resultAffiliateImageId, blob, null, null, uploadedImageIds);
                setResultImageIds((prev) => ({
                  ...prev,
                  [product.id]: resultAffiliateImageId
                })); resultImageBlob = blob;
                resolve();
              }, "image/png");
            };
          });
        }

        const customizedImage = new Image();
        customizedImage.src = URL.createObjectURL(resultImageBlob);
        customizedImage.crossOrigin = "anonymous";

        await new Promise((resolve) => {
          customizedImage.onload = async () => {
            const onProductImage = await getImageFromDB("onProductImages", null, {
              productId: product.id,
              resultImageId: resultAffiliateImageId,
            });

            if (onProductImage) {
              updatedImages[product.id] = URL.createObjectURL(onProductImage);
              return resolve();
            }

            const mockupImage = new Image();
            mockupImage.src = product.image;
            mockupImage.crossOrigin = "anonymous";

            mockupImage.onload = () => {
              canvas.width = mockupImage.width;
              canvas.height = mockupImage.height;

              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.globalCompositeOperation = "source-over";
              ctx.drawImage(mockupImage, 0, 0, canvas.width, canvas.height);

              const overlayWidth = canvas.width * 0.36;
              const overlayHeight = (overlayWidth * customizedImage.height) / customizedImage.width;
              const overlayX = (canvas.width - overlayWidth) / 2;
              const overlayY = (canvas.height - overlayHeight) / 1.65;

              ctx.drawImage(customizedImage, overlayX, overlayY, overlayWidth, overlayHeight);

              canvas.toBlob(async (finalBlob) => {
                if (!finalBlob) return resolve();
                await saveImageToDB("onProductImages", resultAffiliateImageId, finalBlob, product.id, resultAffiliateImageId);
                updatedImages[product.id] = URL.createObjectURL(finalBlob);
                resolve();
              }, "image/png");
            };
          };
        });
      }

      setFinalImages(updatedImages);
    };

    applyAffiliateLogoToProducts();
  }, [products, isProductsLoading, affiliate, isAffiliateLoading, isProductIdsLoading, isProductsMetaLoading, products_meta]);

  const handleAddToCart = (product, isSubscribed = false, quantity = 1, imageId = null, customProductTitle = null) => {
    closeModal();
    dispatch(addToCart({
      ...product,
      Title: customProductTitle || product.title,
      quantity,
      subscription: isSubscribed,
      imageId,
      uploadedImageIds,
    }));

    dispatch(openCart());

    // Events for marketing
    trackCustomEvent('added_to_cart', {
      sku: product.sku,
      price: product.price,
      title: product.title,
      flavor: product.flavor,
      quantity: quantity,
      subscription: isSubscribed,
      context: 'partner\'s product'
    }, affiliate?.code || null);
    fbqTrack('AddToCart', {
      contents: [
        {
          id: product.sku,
          quantity: quantity,
          item_price: product.price,
          title: product.title,
          flavor: product.flavor,
          subscription: isSubscribed
        }
      ],
      content_ids: [product.sku],
      content_type: 'product',
      content_name: product.title,
      content_category: 'partner\'s product',
      value: product.price * quantity,
      currency: 'USD',
      context: 'partner\'s product'
    });
    gtagEvent('add_to_cart', {
      currency: 'USD',
      value: product.price * quantity,
      items: [
        {
          item_id: product.sku,
          item_name: product.title,
          item_category: 'partner\'s product',
          quantity: quantity,
          price: product.price,
          flavor: product.flavor,
          subscription: isSubscribed
        }
      ]
    });
  };

  if (isProductsLoading || isProductIdsLoading || isProductsMetaLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen min-h-screen">
        <Oval visible={true} height={50} width={50} color="#a99cdf" secondaryColor="#e2e6ff" strokeWidth="3" />
      </div>
    );
  }

  return (
    <div className="product-boxes home-slider-container">
      {filteredProducts?.length > 0 && affiliate ? (
        filteredProducts.length <= 6 ? (
          <div className="flex flex-wrap justify-center">
            {filteredProducts.map((product) => (
              <div className="w-[100%] sm:w-[50%] lg:w-[33.33%]">
                <ProductBox
                  key={product.id}
                  product={product}
                  finalImages={finalImages}
                  subscriptions={subscriptions}
                  openModal={openModal}
                  toggleSubscription={toggleSubscription}
                  handleAddToCart={handleAddToCart}
                  resultAffiliateImageId={resultImageIds?.[product.id]}
                  customProductTitle={customMetaByProductId?.[product.id]?.title}
                  fastCheckoutButton={true}
                />
              </div>
            ))}
          </div>
        ) : (
          <Swiper
            modules={[Pagination]}
            pagination={{ el: ".slider-pagination", clickable: true }}
            slidesOffsetAfter={0}
            slidesPerGroup={1}
            breakpoints={{
              300: { slidesPerView: 1 },
              700: { slidesPerView: 2 },
              1000: { slidesPerView: 3 },
            }}
          >
            {productsGrouped(filteredProducts, 2).map((productChunk, index) => (
              <SwiperSlide key={`slide-${index}`}>
                {productChunk.map((product) => (
                  <ProductBox
                    key={product.id}
                    product={product}
                    finalImages={finalImages}
                    subscriptions={subscriptions}
                    openModal={openModal}
                    toggleSubscription={toggleSubscription}
                    handleAddToCart={handleAddToCart}
                    resultAffiliateImageId={resultImageIds?.[product.id]}
                    customProductTitle={customMetaByProductId?.[product.id]?.title}
                    fastCheckoutButton={true}
                  />
                ))}
              </SwiperSlide>
            ))}
            <div className="slider-pagination" />
          </Swiper>
        )
      ) : (
        <p className="text-gray-500 text-center col-span-full color-swap">No products found.</p>
      )}

      <ProductDetailsModal
        isOpen={isProductModalActive}
        onClose={closeModal}
        product={productDetailsModalFor}
        handleAddToCart={handleAddToCart}
        allImages={allImages}
        setAllImages={setAllImages}
        image={affiliatePreviewImage}
        loadingAffiliatePreviewImage={loadingAffiliatePreviewImage}
        setAffiliatePreviewImage={setAffiliatePreviewImage}
        resultAffiliateImageId={resultImageIds}
        customProductsTitles={customMetaByProductId}
      />
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default AffiliateProducts;
